// The social-media copy for one car, in one place.
//
// Pure on purpose — no React, no antd, no `src/config` — so the wording is proved with the bare jest
// react-scripts ships, the same arrangement as ./query.js.
//
// The headline price is the ALL-IN cedi figure the public site shows (buying + shipping + duty +
// clearing). Under it, one note splits that figure into what the customer pays now and what they
// pay before the car lands, because "GH₵ 190,000" on its own reads as money due today. The two
// halves must add up to the headline, so all three come from the same numbers the dashboard's
// "Total (All-in)" box reads.

import { capitalize } from 'lodash'

const nf = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

const CEDI = 'GH₵'

// Hybrids worth saying so in the title. The heading on the detail page lists the plug-ins too, but
// the post has always said plain "Hybrid" for these two and that is a copy decision, not an
// oversight.
const HYBRID_FUELS = ['PETROL-HYBRID', 'DIESEL-HYBRID']

const cedis = amount => `${CEDI} ${nf.format(Number(amount))}`

const titleLine = car => {
  const hybrid =
    car.fuelType && HYBRID_FUELS.includes(car.fuelType.name) ? 'Hybrid' : ''
  return [car.year, car.make && car.make.name, car.model && car.model.name, car.trim, hybrid]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(' ')
    .toUpperCase()
}

const originLine = car => {
  if (!car.origin || !car.origin.country) return ''
  const country =
    car.origin.country.length > 3
      ? capitalize(car.origin.country)
      : car.origin.country.toUpperCase()
  return `Clean car from ${country} ${car.origin.countryFlagEmoji || ''}`.trim()
}

const specLines = car => {
  const lines = []
  const engine = [
    Number(car.engineSize) > 0 ? `${Number(car.engineSize).toFixed(1)}L` : '',
    Number(car.engineCylinders) > 0 ? `${car.engineCylinders}Cyl` : '',
    car.fuelType ? `(${capitalize(car.fuelType.name.replace('-', ' '))})` : ''
  ]
    .filter(Boolean)
    .join(' ')
  if (engine) lines.push(`Engine: ${engine}`)
  if (car.transmission) {
    lines.push(`Transmission: ${capitalize(car.transmission.name)}`)
  }
  if (car.driveTrain) {
    lines.push(`DriveTrain: ${car.driveTrain.name.toUpperCase()}`)
  }
  if (Number(car.mileageMiles) > 0) {
    lines.push(`Mileage: ${nf.format(car.mileageMiles)} Miles`)
  }
  return lines
}

/**
 * The all-in price and the two payments it is made of, or null when we cannot say.
 *
 * Duty needs a body type, an origin, a fuel type and a source currency, so a half-captured car has
 * no clearing figure at all. A post that guessed one would be a price we had quoted publicly and
 * could not honour, so such a car keeps the old price-without-clearing line and gets no note.
 */
export const priceParts = car => {
  const buying = Number(car.price) || 0
  const duty = car.totalEstimatedDuty ? Number(car.totalEstimatedDuty.total) || 0 : 0
  if (buying <= 0 || duty <= 0) return null
  // `landedPriceGhs` is the figure on the site; the sum is only a fallback for an older payload.
  return { buying, duty, allIn: Number(car.landedPriceGhs) || buying + duty }
}

const priceLine = car => {
  const parts = priceParts(car)
  if (parts) {
    return `Total Price: ${cedis(parts.allIn)} (buying, shipping, and clearing)`
  }
  if (Number(car.price) > 0) {
    return `Price: US$ ${nf.format(car.priceUsd)} (~${cedis(car.price)}) (including shipping)`
  }
  return ''
}

const paymentNote = car => {
  const parts = priceParts(car)
  if (!parts) return ''
  return (
    `❗Note: Pay only ${cedis(parts.buying)} for buying and shipping. ` +
    `Then pay ${cedis(parts.duty)} later for duty and clearing charges ` +
    'anytime before the car arrives.'
  )
}

/**
 * The car itself: title, origin, specs, the all-in price, then the note.
 *
 * Built as blocks rather than one template so a field we do not have leaves no gap behind it — a
 * car with no origin used to copy out with a blank line where the country should be.
 */
export const carDetails = car =>
  [[titleLine(car)], [originLine(car)], specLines(car), [priceLine(car)], [paymentNote(car)]]
    .map(block =>
      block
        .map(line => String(line || '').trim())
        .filter(Boolean)
        .join('\n')
    )
    .filter(Boolean)
    .join('\n\n')

export const generalPost = car => `${carDetails(car)}

DM @wabenzy_autos on all socials.
Call or WhatsApp 0245880899
Visit our website: www.wabenzyautos.com
`

export const twitterPost = car => `${carDetails(car)}

Call or WhatsApp 0245880899
More details on our website: https://wabenzyautos.com/cars/${car.slug}
`

export const telegramPost = car => `${carDetails(car)}

Call: 0245880899
WhatsApp: https://wa.me/233245880899
`
